import { matchPath } from 'react-router-dom';
import { MENU_ITEMS } from 'components/sidebar/sidebar.utils';

/**
 * The pages Cinch AI can name and open (CINCHAI-109).
 *
 * One source with the sidebar: the top-level nav pages come straight from MENU_ITEMS with the
 * same `enabled` predicates, so the agent can only offer a page the user could click to. A few
 * pages that are not sidebar items (Billing lives in the settings cog, detail pages) are added
 * here with the same predicate shape. The proxy's `navigate` tool takes a `key` from the list
 * the app sends each turn (`allowedPages`), and the client re-checks before routing.
 *
 * Detail patterns (a journey draft, a segment) only describe where the user is, in words, for
 * the page context; the agent cannot navigate to them (no `navigable`).
 */

const EXTRA_PAGES = [{
  key: '/billing',
  name: 'Billing',
  // Same rule as the settings-cog item in page-header-mui.js.
  enabled: ({
    hasPermission,
    flags,
    isCatalystUser,
    isPLG
  }) => hasPermission('organization.view_billing') && (flags?.billing && !isCatalystUser || isPLG)
}, {
  key: '/cinch-ai',
  name: 'Cinch AI',
  // CINCHAI-122: `cinchAiAllowed` is the provider's one gate (flag `cinch-ai-app`, app installed, allow-list).
  enabled: ({
    isCatalystUser,
    isPLG,
    cinchAiAllowed
  }) => cinchAiAllowed && !isCatalystUser && !isPLG
}];

// Where-am-I descriptions for pages with ids. Order matters: the first match wins.
const DETAIL_PATTERNS = [{
  pattern: '/customer-journeys/draft/:id',
  describe: ({
    id
  }) => `the journey editor (draft ${id})`
}, {
  pattern: '/customer-journeys/:id',
  describe: ({
    id
  }) => `a journey (${id})`
}, {
  pattern: '/segments/:id/edit',
  describe: ({
    id
  }) => `the segment builder, editing segment ${id}`
}, {
  pattern: '/segments/:id',
  describe: ({
    id
  }) => `segment ${id}`
}, {
  pattern: '/create-segment',
  describe: () => 'the segment builder (new segment)'
}, {
  pattern: '/templates/edit/email/:id',
  describe: ({
    id
  }) => `the email template editor (template ${id})`
}, {
  pattern: '/customers/:id',
  describe: ({
    id
  }) => `customer ${id}`
}, {
  pattern: '/cinch-ai/:threadId',
  describe: () => 'the Cinch AI page'
}];
function flattenMenu(items) {
  return items.flatMap(item => [item, ...(item.subMenu ? flattenMenu(item.subMenu) : [])]);
}

/** Every navigable page: `{ key, name, enabled }`, key = route path. */
export const NAVIGABLE_PAGES = [...flattenMenu(MENU_ITEMS).filter(item => typeof item.key === 'string' && item.key.startsWith('/') && !item.externalUrl), ...EXTRA_PAGES].reduce((pages, item) => {
  if (!pages.some(page => page.key === item.key)) {
    pages.push({
      key: item.key,
      name: item.name,
      enabled: item.enabled
    });
  }
  return pages;
}, []);

/** The pages this user may open now. `ctx` is the sidebar's predicate argument. */
export function allowedPages(ctx) {
  return NAVIGABLE_PAGES.filter(page => {
    try {
      return !page.enabled || page.enabled(ctx);
    } catch (e) {
      return false;
    }
  }).map(({
    key,
    name
  }) => ({
    key,
    name
  }));
}
export function findPage(key) {
  return NAVIGABLE_PAGES.find(page => page.key === key);
}

/** "Journeys", "segment 123", or the path itself when nothing matches. */
export function describePath(path) {
  const pathname = (path || '').split('?')[0];
  for (const {
    pattern,
    describe
  } of DETAIL_PATTERNS) {
    const match = matchPath(pathname, {
      path: pattern,
      exact: true
    });
    if (match) return describe(match.params);
  }
  const page = NAVIGABLE_PAGES.filter(p => pathname === p.key || pathname.startsWith(`${p.key}/`)).sort((a, b) => b.key.length - a.key.length)[0];
  return page ? page.name : pathname;
}